import { extendZodWithOpenApi } from '@anatine/zod-openapi';
import {
  DefaultDemoMemorySettings,
  DemoMemorySettingsSchema,
} from '@timelyapp/integration-cloud-sdk/memory-demo-data/types';
import { z } from 'zod';
import { BooleanStrict } from './models.generic';
import { GroupMemoriesBySchema } from './models.tool_configs';

extendZodWithOpenApi(z);

export type MemoryConfig = z.infer<typeof MemoryConfigSchema>;

// Define the LaneConstructorSchema here since it's needed for MemoryConfigSchema
export const LaneConstructorSchema = z
  .enum(['time', 'tool', 'category', 'starred'])
  .openapi({ example: 'tool' })
  .default('time');

// Define MemoryConfigSchema directly with all the properties
export const MemoryConfigSchema = z
  .object({
    /** Determines the strategy to use while building lanes */
    constructLanesOn: LaneConstructorSchema,

    /** If lanes are created with less than this value, they'll be moved to a general time-based lane. Set to 0 to disable */
    minMinutesForDedicatedLane: z.coerce
      .number()
      .int()
      .min(0)
      .max(60 * 8)
      .default(30)
      .openapi({
        description:
          'If lanes are created with less than this value, they will be moved to a general time-based lane. Set to 0 to disable',
        example: 30,
      }),

    /** Inside of lanes, how should Memories be grouped? */
    groupMemoriesBy: GroupMemoriesBySchema,

    /**
     * If distance between two Memories less than this, put the Memory in a lane with available space
     * @todo Client-side, we need to make this dynamic, so very zoomed-in modes don't overlap a ton
     */
    minMinuteGapBetweenMemories: z.coerce.number().int().min(5).max(60).optional().default(5),

    pxPerMinute: z.coerce
      .number()
      .min(1)
      .max(30)
      .int()
      .optional()
      .openapi({
        description: 'Memories that are closely related will merge if overlapping',
        example: 2,
      })
      .default(3),

    minPxPerMinute: z.coerce
      .number()
      .min(1)
      .max(30)
      .int()
      .optional()
      .openapi({
        description: 'Minimum number of pixels per minute. Restricts the minimum zoom level.',
        example: 2,
      })
      .default(2),

    minimumDurationMinutes: z.coerce
      .number()
      .min(0)
      .max(10)
      .openapi({
        description:
          'Memories will be (at minimum) this long. Useful for memories without a duration.',
        example: 0.25,
      })
      .optional()
      .default(0.25),

    minimumGroupDurationMinutes: z.coerce
      .number()
      .int()
      .min(0)
      .max(10)
      .openapi({
        description: `Any MemoryGroup with less than this total duration will be hidden`,
        example: 2,
      })
      .optional()
      .default(2),

    mergeWithinMinutes: z.coerce
      .number()
      .int()
      .optional()
      .openapi({
        description:
          'Allow Memories to expand when a new (similar) Memory is created within this time period.',
        example: 2,
      })
      .default(2),

    /** If true, ignored memories will be returned */
    showIgnored: BooleanStrict.optional().default(false).describe('Show ignored Memories'),

    showOtherMemoriesLane: BooleanStrict.optional()
      .default(true)
      .describe(
        'Turn off to hide "other memories" lane on Timeline; Summary and dedicated lanes remain'
      ),

    compact: BooleanStrict.optional()
      .default(false)
      .describe('Show all Memories as compacted by default'),

    namingStrategy: z.enum(['auto', 'title', 'tool']).default('title').optional().openapi({
      description:
        'Auto depends on `groupMemoriesBy`. Title will use the Memory title. Tool will use the tool name',
      example: 'title',
    }),

    minutesPerBlock: z.coerce
      .number()
      .int()
      .min(0)
      .max(60)
      .default(15)
      .describe('Minutes per summary block; 0 to disable'),

    demoSettings: DemoMemorySettingsSchema.optional().default(DefaultDemoMemorySettings).openapi({
      description: 'Demo memory settings for generating memories with simulated data',
    }),
  })
  .required()
  /**
   * We're allowing arbitrary key/value pairs here,
   * akin to `saveUserProperty` thunk for Timely.
   *
   * This makes config-changing PR's testable, without deploying updated schema.
   *
   * !! NEW PROPERTIES SHOULD STILL BE DEFINED ON SCHEMA !!
   */
  .passthrough();

export type MemoryConfigResponse = z.infer<typeof MemoryConfigResponseSchema>;

export const MemoryConfigResponseSchema = z.object({
  filters: MemoryConfigSchema,
  customized: z
    .boolean()
    .default(false)
    .describe(
      'Indicates whether the user has customized their filters. If false, the default filters are used.'
    ),
});
